<template>
  <div class="impact-page">
    <impact-header title="学生影响明细" :tags="['异动 + 请假 / 旷课汇总到学生', '运营跟进名单']"
                   :chips="chips" :loaded-at="loadedAt"></impact-header>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">核心指标</h2>
        <span class="section-legend">
          <i class="legend-dot is-danger"></i>红=预警
          / <i class="legend-dot is-warning"></i>黄=关注
          / <i class="legend-dot is-healthy"></i>绿=健康
        </span>
      </div>
      <div class="section-filters">
        <filter-pills v-model="days" :options="dayOptions" label="时间窗口" @on-change="reload"></filter-pills>
        <filter-pills v-model="minCount" :options="countOptions" label="受影响" @on-change="reload"></filter-pills>
        <filter-pills v-model="minRemain" :options="remainOptions" label="未消课时" @on-change="reload"></filter-pills>
        <filter-pills v-model="status" :options="statusOptions" label="状态" @on-change="reload"></filter-pills>
      </div>
      <div class="kpi-grid">
        <Spin v-if="loading" fix></Spin>
        <kpi-card v-for="card in kpiCards" :key="card.key" v-bind="card"></kpi-card>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">学生影响清单</h2>
        <span class="section-right">谁该打电话、谁该补课时、谁已经快走了</span>
      </div>
      <div class="grid">
        <div class="card">
          <Spin v-if="loading" fix></Spin>
          <div class="card-head">
            <span class="card-title">学生清单<span class="card-sub">{{ listSub }}</span></span>
            <span class="card-link" :class="{ 'is-disabled': exporting }" @click="doExport">{{ exporting ? '导出中…' : '导出待跟进名单 →' }}</span>
          </div>
          <div class="table-wrap">
            <table class="impact-table">
              <thead>
              <tr>
                <th>学生</th>
                <th>班主任</th>
                <th>主老师</th>
                <th>影响来源明细</th>
                <th class="num sortable" :class="{ 'is-active': sortKey === 'cnt' }" @click="toggleSort('cnt')">
                  受影响次数 <span class="sort-ind">{{ sortInd('cnt') }}</span>
                </th>
                <th class="num sortable" :class="{ 'is-active': sortKey === 'lost' }" @click="toggleSort('lost')">
                  损失课次 <span class="sort-ind">{{ sortInd('lost') }}</span>
                </th>
                <th class="num">剩余课时</th>
                <th>最近受影响</th>
                <th>当前状态</th>
                <th>风险</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in list" :key="row.uid">
                <td><span class="t-name">{{ row.userName || '—' }}</span><span class="t-id">{{ row.uid }}</span></td>
                <td>{{ row.adviserName || '—' }}</td>
                <td>{{ row.tchName || '—' }}</td>
                <td class="wrap">
                  <span v-for="s in (row.sources || [])" :key="s.type" class="mini-badge"
                        :style="sourceStyle(s.type)">{{ typeLabel(s.type) }}×{{ s.count }}</span>
                  <span v-if="!(row.sources || []).length" class="t-muted">—</span>
                </td>
                <td class="num"><b>{{ fmtNum(row.impactCount) }}</b></td>
                <td class="num"><b :class="{ 't-danger': num(row.lostLessons) >= 10 }">{{ fmtNum(row.lostLessons) }}</b></td>
                <td class="num">{{ fmtNum(row.remainHours) }}</td>
                <td>{{ row.lastImpactDate || '—' }}</td>
                <td><span class="badge" :class="statusBadge(row.status)">{{ row.status || '—' }}</span></td>
                <td><span class="badge" :class="levelBadge(row.risk)">{{ row.risk || '—' }}</span></td>
              </tr>
              <tr v-if="!list.length"><td colspan="10" class="empty">当前筛选条件下没有学生</td></tr>
              </tbody>
            </table>
          </div>
          <div class="pager">
            <Page :total="num(d.total)" :current="pageIndex" :page-size="pageSize" :page-size-opts="[20, 50, 100]"
                  show-total show-sizer size="small"
                  @on-change="onPageChange" @on-page-size-change="onPageSizeChange"></Page>
          </div>
        </div>
      </div>

      <div class="grid g2">
        <div class="card">
          <div class="card-head">
            <span class="card-title">损失课次 vs 剩余课时<span class="card-sub">点 = 高频受影响学生，右上需立刻跟进</span></span>
          </div>
          <div v-if="!scatterList.length" class="chart-empty">暂无数据</div>
          <svg v-else class="svg-chart" :viewBox="'0 0 ' + sc.W + ' ' + sc.H">
            <rect :x="sc.zoneX" :y="sc.PT" :width="sc.zoneW" :height="sc.zoneH" fill="#E6464E" fill-opacity="0.05" rx="4"></rect>
            <g v-for="g in sc.yTicks" :key="'y' + g.v">
              <line class="gridline" :x1="sc.PL" :y1="g.y" :x2="sc.W - sc.PR" :y2="g.y"></line>
              <text class="axis-l" :x="sc.PL - 8" :y="g.y + 3" text-anchor="end">{{ g.v }}</text>
            </g>
            <text v-for="g in sc.xTicks" :key="'x' + g.v" class="axis-l" :x="g.x" :y="sc.H - sc.PB + 16" text-anchor="middle">{{ g.v }}</text>
            <circle v-for="p in sc.points" :key="p.uid" :cx="p.x" :cy="p.y" :r="p.danger ? 4.6 : 3.4"
                    :fill="p.danger ? '#E6464E' : '#5C78A8'" :fill-opacity="p.danger ? 0.75 : 0.45"
                    :stroke="p.danger ? '#E6464E' : '#5C78A8'" stroke-width="1.1"><title>{{ p.title }}</title></circle>
            <line :x1="sc.PL" :y1="sc.H - sc.PB" :x2="sc.W - sc.PR" :y2="sc.H - sc.PB" stroke="#DFE4EE"></line>
            <text class="axis-t" :x="(sc.W + sc.PL) / 2" :y="sc.H - 2" text-anchor="middle">损失课次</text>
            <text class="axis-t" :transform="'translate(12,' + (sc.H - sc.PB + sc.PT) / 2 + ') rotate(-90)'" text-anchor="middle">剩余课时</text>
          </svg>
          <div class="note">
            右上区 = 损失课次 ≥ 高频学生人均（{{ fmtNum(sc.lostLine) }}）且剩余课时 ≥ {{ sc.remainLine }}，共 <b>{{ sc.dangerCount }}</b> 人。剩余课时高但损失也高，说明「钱还在、人快走了」，优先用补课 + 固定新老师处理。
          </div>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="card-title">影响来源构成<span class="card-sub">高频受影响学生 · {{ fmtNum(d.highFreqCount) }} 人</span></span>
          </div>
          <bar-list :rows="sourceRows" unit="次" :right-width="110"></bar-list>
          <dl class="kv">
            <dt>事件总数</dt><dd>{{ fmtNum(sourceTotal) }} 次</dd>
            <dt>异动类（离职 / 淘汰 / 关闭时间）</dt><dd>{{ fmtNum(d.changeEventCount) }} 次</dd>
            <dt>请假类（3h内 / 请假 / 旷课）</dt><dd>{{ fmtNum(d.leaveEventCount) }} 次</dd>
            <dt>请假为主的高频学生</dt><dd>{{ fmtRate(d.leaveDominantRate) }}</dd>
          </dl>
          <div class="note is-warning" v-if="num(d.highFreqCount) > 0">
            <b>近 {{ days }} 天 {{ fmtNum(d.highFreqCount) }} 名高频受影响学生中，{{ fmtRate(d.leaveDominantRate) }} 以请假类事件为主。</b>重复请假对同一学生的伤害比一次性离职更隐蔽，建议降低与高频请假老师的排课绑定度。
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ImpactHeader from './parts/ImpactHeader.vue'
import KpiCard from './parts/KpiCard.vue'
import BarList from './parts/BarList.vue'
import FilterPills from './parts/FilterPills.vue'
import {
  postImpact, exportImpact, num, isNum, fmtNum, fmtRate, typeLabel, typeColor, levelLow, sumCount,
  DAY_OPTIONS, LEVEL_BADGE, STATUS_BADGE
} from './parts/impact'

const REMAIN_LINE = 20

function niceMax (v, floor) {
  if (v <= floor) {
    return floor
  }
  let step = Math.pow(10, Math.floor(Math.log10(v)))
  return Math.ceil(v / step) * step
}

export default {
  name: 'TchImpactStudent',
  components: { ImpactHeader, KpiCard, BarList, FilterPills },
  data () {
    return {
      loading: false,
      exporting: false,
      loadedAt: '',
      days: 90,
      minCount: 2,
      minRemain: '',
      status: '',
      sortKey: 'cnt',
      sortDir: 'desc',
      pageIndex: 1,
      pageSize: 20,
      dayOptions: DAY_OPTIONS,
      countOptions: [
        { value: 1, label: '≥ 1 次' },
        { value: 2, label: '≥ 2 次' },
        { value: 3, label: '≥ 3 次' }
      ],
      remainOptions: [
        { value: '', label: '不限' },
        { value: 20, label: '≥ 20' }
      ],
      statusOptions: [
        { value: '', label: '全部' },
        { value: '待换老师', label: '待换老师', color: '#E89F2D' },
        { value: '已换老师', label: '已换老师', color: '#0B9E8D' },
        { value: '正常上课', label: '正常上课', color: '#5B8FF9' },
        { value: '流失预警', label: '流失预警', color: '#E6464E' }
      ],
      d: {}
    }
  },
  computed: {
    filterParams () {
      return {
        days: this.days,
        minCount: this.minCount,
        minRemain: this.minRemain,
        status: this.status,
        sortKey: this.sortKey,
        sortDir: this.sortDir
      }
    },
    list () {
      return this.d.list || []
    },
    scatterList () {
      return this.d.scatter || []
    },
    chips () {
      return [
        { label: '时间窗口', value: '近 ' + this.days + ' 天' },
        { label: '受影响', value: '≥ ' + this.minCount + ' 次' },
        { label: '未消课时', value: this.minRemain === '' ? '不限' : '≥ ' + this.minRemain },
        { label: '状态', value: this.status || '全部' }
      ]
    },
    listSub () {
      return '近 ' + this.days + ' 天 · 按' + (this.sortKey === 'cnt' ? '受影响次数' : '损失课次') +
        (this.sortDir === 'desc' ? '高 → 低' : '低 → 高') + ' · 点击表头切换排序'
    },
    kpiCards () {
      let d = this.d
      return [
        {
          key: 'highFreq',
          title: '高频受影响学生',
          value: fmtNum(d.highFreqCount),
          unit: '人',
          level: levelLow(d.highFreqRate, 5, 10),
          period: '占受影响学生 ' + fmtRate(d.highFreqRate),
          compare: '受影响 ' + fmtNum(d.affectedCount) + ' 人',
          compareTone: 'neutral',
          foot: '近 ' + this.days + ' 天被异动或请假波及 ≥ ' + this.minCount + ' 次'
        },
        {
          key: 'lost',
          title: '累计损失课次',
          value: fmtNum(d.lostTotal),
          unit: '课次',
          level: levelLow(d.lostAvg, 2, 5),
          period: '人均 ' + fmtNum(d.lostAvg) + ' 课次',
          compare: '',
          foot: '高频学生：异动损失课次 + 未代课请假课节'
        },
        {
          key: 'waiting',
          title: '待换老师',
          value: fmtNum(d.waitingCount),
          unit: '人',
          level: num(d.waitingOver7Count) > 0 ? 'danger' : (num(d.waitingCount) > 0 ? 'warning' : 'healthy'),
          period: '异动已生效，未在新老师处上课',
          compare: '超 7 天 ' + fmtNum(d.waitingOver7Count) + ' 人',
          compareTone: num(d.waitingOver7Count) > 0 ? 'danger' : 'neutral',
          foot: '事件已发生但仍未换上新老师'
        },
        {
          key: 'churn',
          title: '高风险流失',
          value: fmtNum(d.churnRiskCount),
          unit: '人',
          level: num(d.churnRiskCount) > 0 ? 'danger' : 'healthy',
          period: '涉及未消课时 ' + fmtNum(d.churnRiskHours),
          compare: '',
          foot: '近 30 天无课 + 剩余课时 > 10 + 受影响 ≥ 2 次，三条同时满足'
        }
      ]
    },
    sourceTotal () {
      return sumCount(this.d.source)
    },
    sourceRows () {
      let total = this.sourceTotal
      return (this.d.source || []).map(function (s) {
        return {
          key: s.type,
          label: typeLabel(s.type),
          value: s.count,
          color: typeColor(s.type),
          extra: total > 0 ? Math.round(num(s.count) / total * 100) + '%' : ''
        }
      })
    },
    sc () {
      let W = 700
      let H = 260
      let PL = 44
      let PR = 16
      let PT = 14
      let PB = 32
      let list = this.scatterList
      let maxL = 0
      let maxR = 0
      list.forEach(function (p) {
        maxL = Math.max(maxL, num(p.lost))
        maxR = Math.max(maxR, num(p.remain))
      })
      let maxX = niceMax(maxL, 10)
      let maxY = niceMax(maxR, 40)
      let sx = function (v) {
        return PL + v / maxX * (W - PL - PR)
      }
      let sy = function (v) {
        return H - PB - v / maxY * (H - PT - PB)
      }
      let lostLine = isNum(this.d.lostAvg) && Number(this.d.lostAvg) > 0 ? Number(this.d.lostAvg) : 5
      let dangerCount = 0
      let points = list.map(function (p) {
        let danger = num(p.lost) >= lostLine && num(p.remain) >= REMAIN_LINE
        if (danger) {
          dangerCount++
        }
        return {
          uid: p.uid,
          x: sx(num(p.lost)),
          y: sy(num(p.remain)),
          danger: danger,
          title: '学生 ' + p.uid + ' · 损失 ' + num(p.lost) + ' · 剩余 ' + num(p.remain)
        }
      })
      let xTicks = []
      let yTicks = []
      for (let i = 0; i <= 4; i++) {
        xTicks.push({ v: Math.round(i / 4 * maxX), x: PL + i / 4 * (W - PL - PR) })
        yTicks.push({ v: Math.round(i / 4 * maxY), y: H - PB - i / 4 * (H - PT - PB) })
      }
      let zoneX = Math.min(sx(lostLine), W - PR)
      let zoneY = Math.max(sy(REMAIN_LINE), PT)
      return {
        W: W,
        H: H,
        PL: PL,
        PR: PR,
        PT: PT,
        PB: PB,
        points: points,
        xTicks: xTicks,
        yTicks: yTicks,
        zoneX: zoneX,
        zoneW: Math.max(0, W - PR - zoneX),
        zoneH: Math.max(0, zoneY - PT),
        lostLine: lostLine,
        remainLine: REMAIN_LINE,
        dangerCount: dangerCount
      }
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    num: num,
    fmtNum: fmtNum,
    fmtRate: fmtRate,
    typeLabel: typeLabel,
    sourceStyle (type) {
      let c = typeColor(type)
      return { color: c, background: c + '1A' }
    },
    levelBadge (level) {
      return LEVEL_BADGE[level] || 'is-gray'
    },
    statusBadge (status) {
      return STATUS_BADGE[status] || 'is-gray'
    },
    sortInd (key) {
      if (this.sortKey !== key) {
        return ''
      }
      return this.sortDir === 'desc' ? '▾' : '▴'
    },
    toggleSort (key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortDir = 'desc'
      }
      this.reload()
    },
    reload () {
      this.pageIndex = 1
      this.load()
    },
    onPageChange (p) {
      if (p === this.pageIndex) {
        return
      }
      this.pageIndex = p
      this.load()
    },
    onPageSizeChange (size) {
      this.pageSize = size
      this.pageIndex = 1
      this.load()
    },
    load () {
      let self = this
      self.loading = true
      let params = Object.assign({ pageIndex: self.pageIndex, pageSize: self.pageSize }, self.filterParams)
      postImpact(self, '/student', params).then(function (data) {
        if (data) {
          self.d = data
          self.loadedAt = self.$moment().format('YYYY-MM-DD HH:mm:ss')
        }
      }).finally(function () {
        self.loading = false
      })
    },
    doExport () {
      if (this.exporting) {
        return
      }
      let self = this
      self.exporting = true
      exportImpact(self, Object.assign({ view: 'student' }, self.filterParams), '学生影响明细.xlsx').finally(function () {
        self.exporting = false
      })
    }
  }
}
</script>
<style scoped lang="less">
@import './parts/page.less';
</style>



// WEBPACK FOOTER //
// TchImpactStudent.vue?394cbe2e